import {
  AfterContentInit,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ArrayFunctions, ObjectFunctions } from '@campus/utils';
import { BehaviorSubject, defer, fromEvent, merge, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import { InputBooleanAttribute, isTruthyInput } from '../core/empty-attribute';
import { SelectableListController } from '../core/selection/selectable-list.controller';
import { SelectionModel } from '../core/selection/selection.model';
import { MenuSelectionChange } from '../menu';
import { RowInterface, RowSelectionChange, TableRowComponent } from './row/row.component';
import {
  GroupedData,
  TableBulkActionInterface,
  TableCellSwitchChange,
  TableColumnConfigInterface,
  TableFilterInterface,
  TableGroupActionInterface,
  TablePaginationInterface,
  TableRowActionInterface,
  TableRowParentInterface,
  TableSelectionChange,
  TableShowOnlySelectionChange,
  TableSortInterface,
  TableVisibleColumnsChange,
} from './table.interface';

const SCROLL_Y_THRESHOLD = 48;
const SCROLL_X_THRESHOLD = 12;

/**
 * You'll probably want to add either the Table2LocalHandlingDirective or the Table2ApiV2Directive to this component.
 * This component only handles presentation, interaction and selection of the table rows, not data management.
 */
@Component({
  standalone: false,
  selector: 'campus-table-v2',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableComponent<T = any>
  implements TableRowParentInterface, OnChanges, OnInit, AfterContentInit, AfterViewInit, OnDestroy
{
  @ViewChildren('rowRef', { read: TableRowComponent }) rows: QueryList<RowInterface<T>>;
  @ViewChild('scrollContainer', { read: ElementRef, static: false }) scrollContainer: ElementRef;

  @HostBinding('class')
  defaultClasses = [
    'cds-table-v2',
    'relative',
    'flex',
    'flex-column',
    'min-block-size-full',
    'border',
    'corner-m',
    'overflow-hidden',
  ];

  //#region Inputs
  @Input() loading = false;

  @Input() data: T[] = [];
  @Input() idKey: Extract<keyof T, number> = 'id' as any;
  @Input() columns: TableColumnConfigInterface<T>[] = [];
  @Input() filters: TableFilterInterface<T>[] = [];
  @Input() sortBy: TableSortInterface<T> = { key: undefined, direction: undefined };
  @Input() groupBy: string;

  @Input() locale = 'nl-BE';
  @Input() headline: string;

  @HostBinding('attr.aria-multiselectable')
  @Input()
  multiple = false;

  @Input() showCheckAll = true;
  @Input() selectable: InputBooleanAttribute = false;
  @Input() disableGrowSearchOnFocus: InputBooleanAttribute;
  @Input() disableTooltipOnTruncated: InputBooleanAttribute;
  @Input() disableTooltip = false;
  @Input() showRefresh: InputBooleanAttribute = false;
  @Input() showSearchTerm = true;
  @Input() showFiltersOnInit = false;

  @Input() pageSize = 10;
  @Input() pagination: TablePaginationInterface;
  @Input() pageSizeOptions: number[] = [10, 25, 50, 100];

  @Input() compareWith: (o1: any, o2: any) => boolean;

  @Input() readonly = false;

  @Input() rowActions: TableRowActionInterface<T>[];
  @Input() groupActions: TableGroupActionInterface<T>[];
  @Input() bulkActions: TableBulkActionInterface<T>[];

  @Input() initialSelection: T[] = [];

  @Input() searchTerm = null;
  @Input() searchTermOnEnter = false;

  @Input() noResultsMessage = 'Geen resultaten gevonden';
  @Input() noDataMessage = 'Geen data gevonden';

  //#endregion

  //#region Public Properties
  @HostBinding('attr.data-has-pagination')
  public hasPagination: boolean | undefined;

  @ContentChild('inlineActions') inlineActionsTemplate?: TemplateRef<any>;

  public dataSource: T[] | GroupedData<T>[] = [];
  public visibleColumns: SelectionModel<TableColumnConfigInterface<T>> = new SelectionModel<
    TableColumnConfigInterface<T>
  >(true);
  public filterableColumns: TableColumnConfigInterface<T>[] = [];
  public selectableColumns: TableColumnConfigInterface<T>[] = [];

  public showFilters = false;
  public filtersForm: FormArray;
  public searchFocus: boolean;
  public columnConfigDict: Record<string, TableColumnConfigInterface<T>> = {};

  public page = 1;
  public totalItems = 0;

  public someVisibleRowsChecked: boolean;
  public allVisibleRowsChecked: boolean;
  public showOnlySelection = false;
  public selectableController: SelectableListController<RowInterface<T>>;

  public hasRowActions = false;
  public hasGroupActions = false;

  public scrolled$: Observable<{ x: boolean; y: boolean }>;
  private _scrolled$ = new BehaviorSubject({ x: false, y: false });

  private searchTermsDebounceTime = 300;
  private searchTerms$: Observable<string>;
  private _searchTerms$ = new Subject<string>();
  private searchTermSubscription: Subscription = new Subscription();
  public searchTermControl = new FormControl('');

  public trackById: (index: number, item: T) => number;

  //#endregion

  //#region Private Properties
  private _ngZone = inject(NgZone);
  private _formBuilder = inject(FormBuilder);
  private _cd = inject(ChangeDetectorRef);
  private _destroyed$ = new Subject<void>();

  private readonly rowsSelectionChange: Observable<RowSelectionChange<T>> = defer(() => {
    const rows = this.rows;

    if (rows) {
      return rows.changes.pipe(
        startWith(rows),
        switchMap(() => merge(...rows.map((row) => row.selectionChange)))
      );
    }

    return this._ngZone.onStable.pipe(
      take(1),
      switchMap(() => this.rowsSelectionChange)
    );
  }) as Observable<RowSelectionChange<T>>;

  //#endregion

  //#region Outputs
  @Output() columnsChange = new EventEmitter<TableColumnConfigInterface<T>[]>();
  @Output() visibleColumnsChange = new EventEmitter<TableVisibleColumnsChange<T>>();
  @Output() paginationChange = new EventEmitter<TablePaginationInterface>();
  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() filtersChange = new EventEmitter<TableFilterInterface<T>[]>();
  @Output() searchTermChange = new EventEmitter<string>();
  @Output() sortChange = new EventEmitter<TableSortInterface<T>>();
  @Output() selectionChange = new EventEmitter<TableSelectionChange>();
  @Output() refresh = new EventEmitter<void>();
  @Output() dataChange = new EventEmitter<GroupedData<T>[] | T[]>();

  @Output() showOnlySelectionChange = new EventEmitter<TableShowOnlySelectionChange>();
  @Output() rowClick = new EventEmitter<T>();
  @Output() cellSwitchChange = new EventEmitter<TableCellSwitchChange<T>>();

  //#endregion

  protected filterOptionCompareWith = (o1: any, o2: any) => {
    const o1Id = o1?.value?.[this.idKey] || o1?.[this.idKey] || o1;
    const o2Id = o2?.value?.[this.idKey] || o2?.[this.idKey] || o2;
    return o1Id === o2Id;
  };

  //#region Lifecycle Hooks

  ngOnChanges(changes: SimpleChanges): void {
    let shouldUpdatePagination = false;

    if (changes.data) {
      shouldUpdatePagination = true;
      this.dataChange.emit(this.data);
    }

    if (changes.columns && changes.columns.currentValue) {
      this._setColumnConfigDict();
      this._setFilterableColumns();
      this._setSelectableColumns();
      if (changes.columns.firstChange || !changes.columns.previousValue) {
        this._initVisibleColumnsSelection();
      } else {
        this._setVisibleColumns();
      }
      this.columnsChange.emit(this.columns);
    }

    if (changes.filters && changes.filters.currentValue) {
      this._initFilterForm(this.filters);
      shouldUpdatePagination = true;
    }

    if (changes.pageSize || changes.pagination) {
      this.hasPagination = !!this.pageSize || !!this.pagination || undefined;
      this.totalItems = this.pagination?.total ?? 0;
      shouldUpdatePagination = true;
    }

    if (changes.pageSize) {
      if (!changes.pageSize.firstChange) {
        this.page = this._getNewPageFromPageSizeChange(
          this.page,
          changes.pageSize.previousValue,
          changes.pageSize.currentValue
        );
      }
      shouldUpdatePagination = true;
    }

    if (changes.multiple) {
      this.multiple = isTruthyInput(this.multiple);
    }

    if (changes.selectable || changes.multiple) {
      this.selectable = isTruthyInput(this.selectable) || this.multiple;
    }

    if (changes.readonly) {
      this.readonly = isTruthyInput(this.readonly);
    }

    if (changes.rowActions) {
      this.hasRowActions = !!this.rowActions?.length;
    }
    if (changes.groupActions) {
      this.hasGroupActions = !!this.groupActions?.length;
    }
    if (changes.disableGrowSearchOnFocus) {
      this.disableGrowSearchOnFocus = isTruthyInput(this.disableGrowSearchOnFocus);
    }

    if (changes.disableTooltipOnTruncated) {
      this.disableTooltipOnTruncated = isTruthyInput(this.disableTooltipOnTruncated);
    }

    if (changes.disableTooltip) {
      this.disableTooltip = isTruthyInput(this.disableTooltip);
    }

    if (changes.idKey) {
      this._setTrackById(this.idKey);
    }

    if (shouldUpdatePagination) {
      this.pagination = this._getPagination(this.page, this.totalItems);
    }

    if (changes.showFiltersOnInit && changes.showFiltersOnInit.firstChange) {
      this.showFilters = this.showFiltersOnInit;
    }
  }

  ngOnInit() {
    this.setupSearchTerms(false);

    if (!this.filtersForm) {
      this._initFilterForm();
    }

    this.scrolled$ = this._scrolled$.pipe(takeUntil(this._destroyed$));

    this._setTrackById(this.idKey);
  }

  ngAfterContentInit(): void {
    this._initSelectableController();
    this.rowsSelectionChange.pipe(takeUntil(this._destroyed$)).subscribe((event) => {
      this._onSelect(event.source, event.isUserInput);
    });
  }

  ngAfterViewInit(): void {
    this.rows.changes.pipe(takeUntil(this._destroyed$)).subscribe(() => {
      if (!this.selectableController) return;

      this.rows.forEach((row) => {
        const isSelected = this.selectableController.isSelected(row);

        if (isSelected !== row.selected) {
          row.select();
        } else if (!isSelected && row.selected) {
          row.deselect();
        }
      });

      const rowsArray = this.rows.toArray();
      this.allVisibleRowsChecked = rowsArray.every((r) => r.selected);
      this.someVisibleRowsChecked = rowsArray.some((r) => r.selected) && !this.allVisibleRowsChecked;

      this._cd.detectChanges();
    });

    // Trigger initial check, fixes the case where rows are already present on init
    // and thus the changes observable doesn't emit
    this.rows.notifyOnChanges();

    fromEvent(this.scrollContainer?.nativeElement, 'scroll')
      .pipe(
        takeUntil(this._destroyed$),
        map((event: Event) => {
          const { scrollLeft, scrollTop } = event.target as HTMLElement;
          return { y: scrollTop > SCROLL_Y_THRESHOLD, x: scrollLeft > SCROLL_X_THRESHOLD };
        })
      )
      .subscribe((scrolled) => this._scrolled$.next(scrolled));
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
    this.searchTermSubscription.unsubscribe();
  }
  //#endregion

  //#region Public Methods
  onVisibleColumnsChange(event: MenuSelectionChange<TableColumnConfigInterface<T>>): void {
    const added = event.added.map((column) => column.value);
    const removed = event.removed.map((column) => column.value);

    event.added.forEach((column) => this.visibleColumns.select(column.value));
    event.removed.forEach((column) => this.visibleColumns.deselect(column.value));

    const changeEvent = new TableVisibleColumnsChange<T>(this.visibleColumns.selected, added, removed);
    this.visibleColumnsChange.emit(changeEvent);
  }

  onSearchTermEnterKeyUp(event: KeyboardEvent): void {
    if (!this.searchTermOnEnter) return;

    this._searchTerms$.next(this.searchTermControl.value);
  }

  onSearchTermFocusOut(event: FocusEvent): void {
    this.searchFocus = false;
    if (!this.searchTermOnEnter) return;

    this._searchTerms$.next(this.searchTermControl.value);
  }

  onSearchTermFocusIn(): void {
    this.searchFocus = true;
  }

  onSearchTermClear(): void {
    if (!this.searchTermOnEnter) return;

    this._searchTerms$.next(this.searchTermControl.value);
  }

  onAddFilter(): void {
    const filter = this._formBuilder.group({
      key: this._formBuilder.control(undefined, { updateOn: 'blur' }), // FormControl for key
      value: this._formBuilder.control(undefined, { updateOn: 'blur' }), // FormControl for value
    });
    this.filtersForm.push(filter);
  }

  onRemoveFilter(index: number): void {
    this.filtersForm.removeAt(index);

    this.filtersChange.emit(this.filtersForm.value);
  }

  onRemoveAllFilters(): void {
    this.filtersForm.clear();

    this.filtersChange.emit(this.filtersForm.value);
  }

  onSortToggle(column: TableColumnConfigInterface<T>): void {
    if (this.sortBy.key !== column.key) {
      this.sortBy.direction = undefined;
    }

    if (this.sortBy.direction === 'asc') {
      this.sortBy.direction = 'desc';
    } else if (this.sortBy.direction === 'desc') {
      this.sortBy.direction = undefined;
    } else {
      this.sortBy.direction = 'asc';
    }

    this.sortBy.key = column.key;

    this.sortChange.emit(this.sortBy);
  }

  onToggleCollapseGroup(group: GroupedData<T>): void {
    group.open = !group.open;
  }

  onPageSizeChange(event: MenuSelectionChange<number>): void {
    if (event.added.length === 0) return;

    const pageSize = event.added[0].value;
    this.pageSize = pageSize || 25;

    this.page = 1;
    this.pagination = this._getPagination(this.page, this.totalItems);

    this.pageSizeChange.emit(this.pageSize);
    this.paginationChange.emit(this.pagination);
  }

  nextPage(): void {
    if (this.page * this.pageSize >= this.totalItems) return;
    this.page++;

    this.pagination = this._getPagination(this.page, this.totalItems);
    this.paginationChange.emit(this.pagination);
  }

  previousPage(): void {
    if (this.page === 1) return;
    this.page--;

    this.pagination = this._getPagination(this.page, this.totalItems);
    this.paginationChange.emit(this.pagination);
  }

  firstPage(): void {
    if (this.page === 1) return;
    this.page = 1;

    this.pagination = this._getPagination(this.page, this.totalItems);
    this.paginationChange.emit(this.pagination);
  }

  onCheckAll(): void {
    if (!this.selectable) return;

    const rows = this.rows.toArray().filter((row) => !row.disabled);

    if (!this.allVisibleRowsChecked) {
      rows.forEach((row) => row.select(true));
    } else {
      rows.forEach((row) => row.deselect(true));

      if (this.showOnlySelection) {
        if (this.selectableController.selected.length === 0) return this.onToggleShowOnlySelection();

        this._cd.detectChanges(); // update rows array
        if (this.rows.length === 0) return this.previousPage();
      }
    }
  }

  setShowOnlySelection(showOnlySelection: boolean): void {
    if (this.showOnlySelection === showOnlySelection) return;

    this.onToggleShowOnlySelection();
  }

  onToggleShowOnlySelection() {
    this.showOnlySelection = !this.showOnlySelection;
    this.firstPage();

    const event = new TableShowOnlySelectionChange(
      this.showOnlySelection,
      this.selectableController.selected.map((s) => s.value[this.idKey] as number),
      this.visibleColumns.selected
    );
    this.showOnlySelectionChange.emit(event);
  }

  onRefresh(event: MouseEvent) {
    this.refresh.emit();
  }

  onRowClick(event: PointerEvent, row: T): void {
    if (event.target instanceof HTMLAnchorElement) return;

    if (this.readonly) return;

    this.rowClick.emit(row);
  }

  onRowActionClick(action: TableRowActionInterface<T>, row: T): void {
    action.click(row);
  }

  onCellSwitchChange(row: T, column: TableColumnConfigInterface<T>, value: boolean): void {
    this.cellSwitchChange.emit(new TableCellSwitchChange<T>(row, column, value));
  }

  onGroupActionClick(action: TableGroupActionInterface<T>, group: T): void {
    action.click(group, this);
  }

  onBulkClick(action: TableBulkActionInterface<T>): void {
    action.click(this.selectableController.selected.map((s) => s.value));
  }

  clearSelection() {
    this.selectableController?.clear();
    this.setShowOnlySelection(false);
  }

  deselectRows(ids: number[]) {
    const selection = this.selectableController.selected.filter((row) => ids.includes(row.value[this.idKey as any]));
    if (selection.length) this.selectableController.deselect(...selection);
    if (this.selectableController.isEmpty()) this.setShowOnlySelection(false);
  }

  getRowActions(row: T): TableRowActionInterface<T>[] {
    return this.rowActions?.filter((action) => !action.condition || action.condition(row));
  }

  getGroupActions(group: T): TableGroupActionInterface<T>[] {
    return this.groupActions?.filter((action) => !action.condition || action.condition(group));
  }

  getBulkActions(): TableBulkActionInterface<T>[] {
    return this.bulkActions?.filter(
      (action) => !action.condition || action.condition(this.selectableController.selected)
    );
  }

  filterSelect(formGroup: FormGroup, index: number) {
    const { key, value } = formGroup.value;

    if (!key && !value) return;

    const config = this.columnConfigDict[key];
    const control = this.filtersForm.controls[index];
    const hooks = control.get('value').updateOn;
    const targetUpdateOn = config.filterOnBlur ? 'blur' : 'change';
    if (hooks === targetUpdateOn) return;

    const newControl = this._formBuilder.group({
      key: this._formBuilder.control(key, { updateOn: targetUpdateOn }),
      value: this._formBuilder.control(value, { updateOn: targetUpdateOn }),
    });

    this.filtersForm.setControl(index, newControl, { emitEvent: false });
  }

  //#endregion

  //#region Private Methods

  private _initVisibleColumnsSelection(): void {
    const visibleColumns = this.columns.filter((column) => !!column.visible || !!column.alwaysVisible);
    this.visibleColumns = new SelectionModel<TableColumnConfigInterface<T>>(true, visibleColumns, false);
  }

  private _setVisibleColumns(): void {
    this.visibleColumns.setSelection(...this.columns.filter((column) => !!column.visible || !!column.alwaysVisible));
  }

  private _setColumnConfigDict(): void {
    this.columnConfigDict = this.columns.reduce(
      (acc, column) => {
        acc[column.key as string] = column;
        return acc;
      },
      {} as Record<string, TableColumnConfigInterface<T>>
    );
  }

  private _setFilterableColumns(): void {
    this.filterableColumns = this.columns.filter(
      (column) => column.filterable === undefined || column.filterable === true
    );
  }

  private _setSelectableColumns(): void {
    this.selectableColumns = this.columns.filter((column) => !column.hidden && !column.alwaysVisible);
  }

  private _initFilterForm(initialFilters: TableFilterInterface<T>[] = []): void {
    const isNewForm = !this.filtersForm;
    if (isNewForm) {
      this.filtersForm = this._formBuilder.array([]);
    } else {
      this.filtersForm.clear({ emitEvent: false });
    }

    initialFilters.forEach((_filter) => {
      this.filtersForm.push(
        this._formBuilder.group({
          key: this._formBuilder.control(_filter.key, {
            updateOn: this.columnConfigDict[_filter.key as string].filterOnBlur ? 'blur' : 'change',
          }),
          value: this._formBuilder.control(_filter.value, {
            updateOn: this.columnConfigDict[_filter.key as string].filterOnBlur ? 'blur' : 'change',
          }),
        }),
        { emitEvent: false }
      );
    });

    this.filterUnfilterableColumns();

    this.filtersForm.markAsPristine();
    this.filtersForm.updateValueAndValidity({ emitEvent: true });

    if (!isNewForm) return;

    this.filtersForm.valueChanges
      .pipe(
        takeUntil(this._destroyed$),
        startWith(this.filtersForm.value),
        distinctUntilChanged((filters1: TableFilterInterface[], filters2: TableFilterInterface[]) => {
          const isNotEmpty = (f: TableFilterInterface) =>
            f.key !== null && (f.value !== null || this.columnConfigDict[f.key as string]?.type === 'boolean');
          filters1 = filters1.filter(isNotEmpty);
          filters2 = filters2.filter(isNotEmpty);
          return (
            filters1.length === filters2.length && filters1.every((f, i) => ObjectFunctions.deepEquals(f, filters2[i]))
          );
        })
      )
      .subscribe((filters: TableFilterInterface[]) => {
        filters.forEach((_filter) => {
          if (this.columnConfigDict[_filter.key as string]?.type === 'boolean' && _filter.value == null) {
            _filter.value = false;
          }
        });

        this.page = 1;
        this.pagination = this._getPagination(this.page, this.totalItems);

        this.filtersChange.emit(filters);
        this.paginationChange.emit(this.pagination);
      });
  }

  private filterUnfilterableColumns(): void {
    this.filtersForm.value.forEach((filter) => {
      if (!this.filterableColumns.some((column) => column.key === filter.key)) {
        this.filtersForm.removeAt(
          this.filtersForm.value.findIndex((f) => f.key === filter.key),
          { emitEvent: false }
        );
      }
    });
  }

  private _getPagination(page: number, total: number): TablePaginationInterface {
    const pageSize = this.pageSize ?? 10;
    const from = (page - 1) * pageSize;
    const to = from + pageSize;

    const pagination = {
      from: total ? from + 1 : 0,
      to: Math.min(to, total),
      total,
    };

    return pagination;
  }

  private _getNewPageFromPageSizeChange(currentPage: number, previousPageSize: number, newPageSize: number): number {
    const currentFrom = (currentPage - 1) * previousPageSize;
    return Math.floor(currentFrom / newPageSize) + 1;
  }

  private _onSelect(row: RowInterface<T>, isUserInput: boolean): void {
    const wasSelected = this.selectableController.isSelected(row);

    if (wasSelected !== row.selected) {
      if (!this.multiple) {
        this.selectableController.clear();
      }
      if (row.selected) this.selectableController.select(row);
      else this.selectableController.deselect(row);
    }
  }

  private _getCompareWith(): (o1: any, o2: any) => boolean {
    const compareWith =
      this.compareWith ||
      ((o1: RowInterface<T>, o2: RowInterface<T>) => {
        return o1.value[this.idKey] === o2.value[this.idKey];
      });

    return compareWith;
  }

  selectByData(rowData: T[]) {
    this.selectableController.setRawSelection(...rowData);
  }

  deselectByData(rowData: T[]) {
    this.selectableController.deselect(...(rowData as any));
  }

  private _initSelectableController(): void {
    const initialSelection: RowInterface<T>[] = ArrayFunctions.wrapIntoArray(this.initialSelection).map(
      (is) => ({ value: is }) as RowInterface<T>
    );

    this.selectableController = new SelectableListController(
      isTruthyInput(this.multiple),
      initialSelection,
      true,
      this._getCompareWith()
    );

    this.selectableController.changed.pipe(takeUntil(this._destroyed$)).subscribe((event) => {
      const asSelectable = (row: RowInterface<T>) => {
        if (this.selectableController.isSelectable(row)) return row;
        return this.rows.find((r) => this.selectableController.compareWith(r, row));
      };
      event.added.map(asSelectable).forEach((row) => row?.select());
      event.removed.map(asSelectable).forEach((row) => row?.deselect());

      const { selected } = event;
      const selection = {
        selected: this.multiple ? selected.map((s) => s.value) : selected[0]?.value,
        added: event.added.map((s) => s.value),
        removed: event.removed.map((s) => s.value),
      };

      this.allVisibleRowsChecked = this.rows.toArray().every((r) => r.selected);
      this.someVisibleRowsChecked = this.rows.toArray().some((r) => r.selected) && !this.allVisibleRowsChecked;

      this.selectionChange.emit(new TableSelectionChange(selection.selected, selection.added, selection.removed));
    });
  }

  private setupSearchTerms(shouldDebounce?: boolean): void {
    this.searchTerms$ = this.searchTermOnEnter
      ? this._searchTerms$
      : shouldDebounce
        ? this.searchTermControl.valueChanges.pipe(debounceTime(this.searchTermsDebounceTime))
        : this.searchTermControl.valueChanges;

    this.searchTermControl.setValue(this.searchTerm, { emitEvent: false });

    this.searchTermSubscription.unsubscribe();
    this.searchTermSubscription = new Subscription();

    this.searchTermSubscription.add(
      this.searchTerms$.subscribe((term) => {
        this.searchTerm = term;

        this.page = 1;
        this.pagination = this._getPagination(this.page, this.totalItems);

        this.searchTermChange.emit(term);
        this.paginationChange.emit(this.pagination);
      })
    );
  }

  private _setTrackById(idKey: Extract<keyof T, number>) {
    this.trackById = function (_, item) {
      return item[idKey] as number;
    };
  }
  //#endregion
}
